"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { toast } from "sonner";
import { ForeignNationalsAPI } from "../../../api/foreignNationals";
import {
  AssociateIndividualsRequest,
  CreateForeignNationalRequest,
  ForeignNational,
  UserProfile,
  UpdateForeignNationalRequest
} from "../../../api/types";
import { IndividualApiResponse } from "../../../api/individuals";

type ForeignNationalsContextValue = {
  items: ForeignNational[];
  filteredItems: ForeignNational[];
  selectedForeignNational: ForeignNational | null;
  isLoadingList: boolean;
  isLoadingDetail: boolean;
  isSaving: boolean;
  error: Error | null;
  searchInput: string;
  refreshNonce: number;
  setSearch: (value: string) => void;
  fetchForeignNationals: () => Promise<void>;
  fetchForeignNational: (foreignNationalId: string) => Promise<void>;
  createForeignNational: (
    payload: CreateForeignNationalRequest
  ) => Promise<ForeignNational>;
  associateIndividuals: (
    foreignNationalId: string,
    payload: AssociateIndividualsRequest
  ) => Promise<IndividualApiResponse[]>;
  updateForeignNational: (
    foreignNationalId: string,
    payload: UpdateForeignNationalRequest
  ) => Promise<ForeignNational>;
  refresh: () => void;
  clearSelectedForeignNational: () => void;
};

const ForeignNationalsContext =
  createContext<ForeignNationalsContextValue | null>(null);

const resolveToastDescription = (
  variant: "success" | "error" | "neutral" | "brand",
  title: string,
  description?: string
): string => {
  const d = (description ?? "").trim();
  if (d) return d;
  if (variant === "error") return "Please try again.";
  if (variant === "success") return "Completed successfully.";
  if (variant === "neutral") return "Updated.";
  return title.trim() || "Done.";
};

const showToast = (
  variant: "success" | "error" | "neutral" | "brand",
  title: string,
  description?: string
) => {
  const notify =
    variant === "error"
      ? toast.error
      : variant === "neutral"
      ? toast.info
      : toast.success;
  notify(title, {
    description: resolveToastDescription(variant, title, description),
    duration: 5000
  });
};

const showClientCreatedToast = () => {
  toast.success("Client created", { duration: 5000 });
};

const matchesQuery = (item: ForeignNational, query: string) => {
  if (!query) return true;

  const haystack = [
    item.first_name,
    item.middle_name,
    item.last_name,
    item.email,
    item.phone,
    item.practice_name
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(query);
};

const normalizeUserProfile = (value: unknown): UserProfile | null => {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  const uid = typeof v.uid === "string" ? v.uid : "";
  if (!uid.trim()) return null;

  const first_name = typeof v.first_name === "string" ? v.first_name : null;
  const last_name = typeof v.last_name === "string" ? v.last_name : null;
  const email = typeof v.email === "string" ? v.email : null;
  const avatar_data_url =
    typeof v.avatar_data_url === "string" ? v.avatar_data_url : null;

  return {
    uid,
    first_name,
    last_name,
    email,
    avatar_data_url
  };
};

const normalizeForeignNational = (item: ForeignNational): ForeignNational => {
  const assignedRaw = (item as unknown as { assigned_to_user_ids?: unknown })
    .assigned_to_user_ids;
  const assigned =
    Array.isArray(assignedRaw) &&
    assignedRaw.every((value) => typeof value === "string")
      ? assignedRaw
      : Array.isArray(assignedRaw)
      ? assignedRaw.filter(
          (value): value is string => typeof value === "string"
        )
      : [];

  const profilesRaw = (
    item as unknown as { assigned_to_user_profiles?: unknown }
  ).assigned_to_user_profiles;
  const assignedProfiles = Array.isArray(profilesRaw)
    ? profilesRaw.map(normalizeUserProfile)
    : [];

  return {
    ...item,
    first_name: typeof item.first_name === "string" ? item.first_name : "",
    last_name: typeof item.last_name === "string" ? item.last_name : "",
    email: typeof item.email === "string" ? item.email : "",
    practice_name:
      typeof item.practice_name === "string" ? item.practice_name : "",
    assigned_to_user_ids: assigned,
    assigned_to_user_profiles: assignedProfiles
  };
};

export const useForeignNationals = () => {
  const context = useContext(ForeignNationalsContext);
  if (!context) {
    throw new Error(
      "useForeignNationals must be used within a ForeignNationalsProvider"
    );
  }
  return context;
};

export type ForeignNationalsProviderMode = "list" | "self";

export const ForeignNationalsProvider: React.FC<{
  children: React.ReactNode;
  mode?: ForeignNationalsProviderMode;
}> = ({ children, mode = "list" }) => {
  const [items, setItems] = useState<ForeignNational[]>([]);
  const [selectedForeignNational, setSelectedForeignNational] =
    useState<ForeignNational | null>(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [refreshNonce, setRefreshNonce] = useState(0);

  const fetchForeignNationals = useCallback(async () => {
    setIsLoadingList(true);
    setError(null);
    try {
      const limit = 200;
      let offset = 0;
      const all: ForeignNational[] = [];

      // Fetch all pages (used by any list-mode experiences).
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const response = await ForeignNationalsAPI.list({ limit, offset });
        const page = Array.isArray(response.foreign_nationals)
          ? response.foreign_nationals
          : [];
        all.push(...page);
        if (page.length < limit) break;
        offset += limit;
      }

      setItems(all.map(normalizeForeignNational));
    } catch (err) {
      const nextError =
        err instanceof Error ? err : new Error("Failed to load clients");
      setItems([]);
      setError(nextError);
      showToast("error", "Error loading clients", nextError.message);
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  const fetchForeignNational = useCallback(
    async (foreignNationalId: string) => {
      setIsLoadingDetail(true);
      setError(null);
      try {
        const response = normalizeForeignNational(
          await ForeignNationalsAPI.get(foreignNationalId)
        );
        setSelectedForeignNational(response);
        setItems((prev) => {
          const exists = prev.some((item) => item.uid === response.uid);
          if (!exists) return [response, ...prev];
          return prev.map((item) =>
            item.uid === response.uid ? response : item
          );
        });
      } catch (err) {
        const nextError =
          err instanceof Error ? err : new Error("Failed to load client");
        setError(nextError);
        showToast("error", "Error loading client", nextError.message);
        throw nextError;
      } finally {
        setIsLoadingDetail(false);
      }
    },
    []
  );

  const createForeignNational = useCallback(
    async (payload: CreateForeignNationalRequest) => {
      setIsSaving(true);
      setError(null);
      try {
        const created = normalizeForeignNational(
          await ForeignNationalsAPI.create(payload)
        );
        setSelectedForeignNational(created);
        setItems((prev) => [created, ...prev]);
        setRefreshNonce((prev) => prev + 1);
        showClientCreatedToast();
        return created;
      } catch (err) {
        const nextError =
          err instanceof Error ? err : new Error("Failed to create client");
        setError(nextError);
        showToast("error", "Error creating client", nextError.message);
        throw nextError;
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  const associateIndividuals = useCallback(
    async (
      foreignNationalId: string,
      payload: AssociateIndividualsRequest
    ): Promise<IndividualApiResponse[]> => {
      setIsSaving(true);
      setError(null);
      try {
        const response = await ForeignNationalsAPI.associateIndividuals(
          foreignNationalId,
          payload
        );
        showToast(
          "success",
          "Individuals associated",
          "Individuals were successfully associated with this client."
        );
        return response;
      } catch (err) {
        const nextError =
          err instanceof Error
            ? err
            : new Error("Failed to associate individuals");
        setError(nextError);
        showToast("error", "Error associating individuals", nextError.message);
        throw nextError;
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  const updateForeignNational = useCallback(
    async (
      foreignNationalId: string,
      payload: UpdateForeignNationalRequest
    ): Promise<ForeignNational> => {
      setIsSaving(true);
      setError(null);
      try {
        const updated = normalizeForeignNational(
          await ForeignNationalsAPI.update(foreignNationalId, payload)
        );

        setSelectedForeignNational((prev) =>
          prev?.uid === updated.uid ? updated : prev
        );
        setItems((prev) =>
          prev.map((item) => (item.uid === updated.uid ? updated : item))
        );
        return updated;
      } catch (err) {
        const nextError =
          err instanceof Error ? err : new Error("Failed to update client");
        setError(nextError);
        showToast("error", "Error updating client", nextError.message);
        throw nextError;
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  const refresh = useCallback(() => {
    setRefreshNonce((prev) => prev + 1);
  }, []);

  const clearSelectedForeignNational = useCallback(() => {
    setSelectedForeignNational(null);
  }, []);

  useEffect(() => {
    if (mode === "self") return;
    fetchForeignNationals();
  }, [fetchForeignNationals, mode, refreshNonce]);

  const filteredItems = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    return items.filter((item) => matchesQuery(item, query));
  }, [items, searchInput]);

  const value = useMemo(
    () => ({
      items,
      filteredItems,
      selectedForeignNational,
      isLoadingList,
      isLoadingDetail,
      isSaving,
      error,
      searchInput,
      refreshNonce,
      setSearch: setSearchInput,
      fetchForeignNationals,
      fetchForeignNational,
      createForeignNational,
      associateIndividuals,
      updateForeignNational,
      refresh,
      clearSelectedForeignNational
    }),
    [
      items,
      filteredItems,
      selectedForeignNational,
      isLoadingList,
      isLoadingDetail,
      isSaving,
      error,
      searchInput,
      refreshNonce,
      fetchForeignNationals,
      fetchForeignNational,
      createForeignNational,
      associateIndividuals,
      updateForeignNational,
      refresh,
      clearSelectedForeignNational
    ]
  );

  return (
    <ForeignNationalsContext.Provider value={value}>
      {children}
    </ForeignNationalsContext.Provider>
  );
};
